/**
 * CarbonSurfaceThemeProvider — Surface Layer (/01)
 *
 * Wraps the app in Carbon `<Theme>` and injects a single dynamic
 * `<style>` tag at `<head>` that emits the merged token overrides as
 * CSS custom properties on `:root`.
 *
 * Reads tenant override + user preference via persistence hooks (mocked
 * today). Listens for SessionThemeEdit dispatches from the Settings
 * tearsheet via a small Zustand store. Emits THEME_UPDATED on every
 * resolved change via the WebKit bridge.
 *
 * NO inline styles outside the override injection node.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { Theme } from '@carbon/react';
import { create } from 'zustand';
import type {
  MergedThemePayload,
  SessionThemeEdit,
  TenantThemeOverride,
  UserThemePreference,
} from '@contracts/theme.contract';
import { mergeThemePayload } from './merge-theme-payload';
import { validateThemePayload } from './validate-theme-payload';
import { emit as emitBridgeEvent } from '@lib/webkit/messageHandler';
import { useLocalDeviceStore } from '@lib/persistence/useLocalDeviceStore';

interface SessionThemeStoreState {
  edit: SessionThemeEdit | null;
  setEdit: (next: SessionThemeEdit | null) => void;
  patchEdit: (patch: Partial<SessionThemeEdit>) => void;
}

export const useSessionThemeStore = create<SessionThemeStoreState>((set) => ({
  edit: null,
  setEdit: (next) => set({ edit: next }),
  patchEdit: (patch) =>
    set((state) => ({
      edit: {
        ...state.edit,
        ...patch,
      } as SessionThemeEdit,
    })),
}));

interface ThemeContextValue {
  merged: MergedThemePayload;
  setUserPreference: (next: UserThemePreference | null) => void;
  setTenantOverride: (next: TenantThemeOverride | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useSurfaceTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useSurfaceTheme must be used inside <CarbonSurfaceThemeProvider />');
  }
  return ctx;
}

interface ProviderProps {
  initialTenant?: TenantThemeOverride | null;
  initialUser?: UserThemePreference | null;
  children: ReactNode;
}

const STYLE_NODE_ID = 'surface-theme-overrides';
const EMIT_DEBOUNCE_MS = 50;

export function CarbonSurfaceThemeProvider({
  initialTenant = null,
  initialUser = null,
  children,
}: ProviderProps) {
  const [tenant, setTenantOverrideRaw] = useLocalDeviceStore<TenantThemeOverride | null>(
    'theme.tenant',
    initialTenant
  );
  const [user, setUserPreferenceRaw] = useLocalDeviceStore<UserThemePreference | null>(
    'theme.user',
    initialUser
  );
  const session = useSessionThemeStore((s) => s.edit);

  const merged = useMemo<MergedThemePayload>(
    () => mergeThemePayload({ tenant, user, session }),
    [tenant, user, session]
  );

  // Build the :root rule from accepted tokens.
  const css = useMemo(() => {
    const declarations: string[] = [];
    for (const [k, v] of Object.entries(merged.tokens)) {
      if (v != null) declarations.push(`  --${k}: ${v};`);
    }
    for (const [k, v] of Object.entries(merged.surfaceTokens)) {
      if (v != null) declarations.push(`  --${k}: ${v};`);
    }
    return `:root {\n${declarations.join('\n')}\n}\n`;
  }, [merged.tokens, merged.surfaceTokens]);

  // Inject a single <style> node and update it on changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let node = document.getElementById(STYLE_NODE_ID) as HTMLStyleElement | null;
    if (!node) {
      node = document.createElement('style');
      node.id = STYLE_NODE_ID;
      node.setAttribute('data-managed-by', 'CarbonSurfaceThemeProvider');
      document.head.appendChild(node);
    }
    node.textContent = css;
    // Also set data-carbon-theme on <html> for any CSS that keys off it.
    document.documentElement.dataset.carbonTheme = merged.baseTheme;
    return () => {
      // Do not remove on unmount in normal usage; only remove on full app teardown.
    };
  }, [css, merged.baseTheme]);

  // Debounced WebKit emit on merged-payload change.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      emitBridgeEvent({ type: 'THEME_UPDATED', payload: merged });
    }, EMIT_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [merged]);

  const setUserPreference = useCallback(
    (next: UserThemePreference | null) => {
      if (next) {
        const v = validateThemePayload({ tokens: next.tokens, surfaceTokens: next.surfaceTokens });
        if (v.rejected.length > 0) {
          for (const r of v.rejected) {
            emitBridgeEvent({
              type: 'THEME_REJECTED',
              payload: { offendingKey: r.key, reason: r.reason },
            });
          }
        }
      }
      setUserPreferenceRaw(next);
    },
    [setUserPreferenceRaw]
  );

  const setTenantOverride = useCallback(
    (next: TenantThemeOverride | null) => {
      if (next) {
        const v = validateThemePayload({ tokens: next.tokens, surfaceTokens: next.surfaceTokens });
        if (v.rejected.length > 0) {
          for (const r of v.rejected) {
            emitBridgeEvent({
              type: 'THEME_REJECTED',
              payload: { offendingKey: r.key, reason: r.reason },
            });
          }
        }
      }
      setTenantOverrideRaw(next);
    },
    [setTenantOverrideRaw]
  );

  const ctxValue = useMemo<ThemeContextValue>(
    () => ({
      merged,
      setUserPreference,
      setTenantOverride,
    }),
    [merged, setUserPreference, setTenantOverride]
  );

  return (
    <ThemeContext.Provider value={ctxValue}>
      <Theme theme={merged.baseTheme} className="surface-theme-root">
        {children}
      </Theme>
    </ThemeContext.Provider>
  );
}
